// src/components/CMOnboardingFlow.jsx — GoldenScope Contract Manager Onboarding
import { useState } from 'react';
import supabase from '../utils/supabaseClient';
import { cmProfilesDb } from '../utils/store';

const STEPS = [
  { id: 'welcome',  icon: '✒',  title: 'Welcome, Contract Manager' },
  { id: 'profile',  icon: '👤', title: 'Set Up Your Profile'       },
  { id: 'done',     icon: '🎉', title: "You're Ready"               },
];

export default function CMOnboardingFlow({ profile, cmProfile, onComplete, onSignOut }) {
  const [step, setStep]     = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  // Profile fields
  const [name,     setName]     = useState(cmProfile?.name     || profile?.contactName || '');
  const [title,    setTitle]    = useState(cmProfile?.title    || '');
  const [phone,    setPhone]    = useState(cmProfile?.phone    || '');
  const [email,    setEmail]    = useState(cmProfile?.email    || profile?.contactEmail || '');
  const [division,   setDivision]   = useState(cmProfile?.division   || '');
  const [agencyName, setAgencyName] = useState(cmProfile?.agency_name || profile?.agencyName || '');

  // Multiple addresses
  const [addresses, setAddresses] = useState(
    cmProfile?.addresses?.length
      ? cmProfile.addresses
      : [{ label: 'Main Office', street: '', city: '', state: 'CA', zip: '' }]
  );

  const current = STEPS[step];
  const isLast  = step === STEPS.length - 1;

  // ── Address helpers ───────────────────────────────────────────────────
  function addAddress() {
    setAddresses(a => [...a, { label: '', street: '', city: '', state: 'CA', zip: '' }]);
  }

  function updateAddress(i, field, val) {
    setAddresses(a => a.map((addr, idx) => idx === i ? { ...addr, [field]: val } : addr));
  }

  function removeAddress(i) {
    setAddresses(a => a.filter((_, idx) => idx !== i));
  }

  // ── Save profile to cm_profiles ───────────────────────────────────────
  async function saveProfile() {
    if (!name.trim()) { setError('Name is required'); return false; }
    setSaving(true); setError('');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get the cm_profile_id from user_profiles
      const { data: prof } = await supabase
        .from('user_profiles')
        .select('cm_profile_id')
        .eq('id', user.id)
        .single();

      if (!prof?.cm_profile_id) throw new Error('CM profile not found. Please contact support.');

      const updates = {
        name:        name.trim(),
        title:       title.trim()       || null,
        phone:       phone.trim()       || null,
        email:       email.trim()       || null,
        division:    division.trim()    || null,
        agency_name: agencyName.trim()  || null,
        addresses:   addresses.filter(a => a.street.trim()),
        updated_at:  new Date().toISOString(),
      };

      const { error: updateError } = await supabase
        .from('cm_profiles')
        .update(updates)
        .eq('id', prof.cm_profile_id);

      if (updateError) throw updateError;

      // Sync changes back to any analyst CM roster entries
      await cmProfilesDb.syncToRoster(prof.cm_profile_id, updates);

      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function handleNext() {
    if (current.id === 'profile') {
      const ok = await saveProfile();
      if (!ok) return;
    }
    if (isLast) { onComplete(); return; }
    setStep(s => s + 1);
  }

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #0f1f3d 0%, #1a3260 60%, #0f1f3d 100%)',
      fontFamily: 'Inter, system-ui, sans-serif', padding: 24,
    }}>
      <div style={{ width: '100%', maxWidth: 560 }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 56, height: 56, borderRadius: 14,
            background: 'linear-gradient(135deg, #c49922 0%, #e8c04a 100%)',
            marginBottom: 12,
            boxShadow: '0 8px 32px rgba(196,153,34,0.4)',
            padding: 8,
          }} dangerouslySetInnerHTML={{ __html: '<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 32 32\" width=\"100%\" height=\"100%\"><rect x=\"2\" y=\"13\" width=\"18\" height=\"6\" rx=\"3\" fill=\"url(#ab1)\"/><rect x=\"17\" y=\"14\" width=\"7\" height=\"4\" rx=\"2\" fill=\"url(#ab2)\"/><rect x=\"22\" y=\"14.5\" width=\"7\" height=\"3\" rx=\"1.5\" fill=\"url(#ab3)\"/><circle cx=\"4\" cy=\"16\" r=\"3.5\" fill=\"none\" stroke=\"#2A1200\" stroke-width=\"1.5\"/><circle cx=\"4\" cy=\"16\" r=\"2\" fill=\"url(#alens)\" opacity=\"0.85\"/><rect x=\"8\" y=\"13\" width=\"1.5\" height=\"6\" rx=\"0.5\" fill=\"url(#aband)\"/><rect x=\"14\" y=\"13\" width=\"1.5\" height=\"6\" rx=\"0.5\" fill=\"url(#aband)\"/><rect x=\"19\" y=\"14\" width=\"1\" height=\"4\" rx=\"0.4\" fill=\"url(#aband)\"/><line x1=\"24\" y1=\"14.8\" x2=\"24\" y2=\"17.2\" stroke=\"#1A0A00\" stroke-width=\"0.6\" opacity=\"0.5\"/><line x1=\"25.5\" y1=\"14.8\" x2=\"25.5\" y2=\"17.2\" stroke=\"#1A0A00\" stroke-width=\"0.6\" opacity=\"0.5\"/><defs><linearGradient id=\"ab1\" x1=\"0\" y1=\"0\" x2=\"0\" y2=\"1\"><stop offset=\"0%\" stop-color=\"#6B3A00\"/><stop offset=\"40%\" stop-color=\"#3D2000\"/><stop offset=\"100%\" stop-color=\"#1A0A00\"/></linearGradient><linearGradient id=\"ab2\" x1=\"0\" y1=\"0\" x2=\"0\" y2=\"1\"><stop offset=\"0%\" stop-color=\"#5A3000\"/><stop offset=\"50%\" stop-color=\"#3A1A00\"/><stop offset=\"100%\" stop-color=\"#1A0800\"/></linearGradient><linearGradient id=\"ab3\" x1=\"0\" y1=\"0\" x2=\"0\" y2=\"1\"><stop offset=\"0%\" stop-color=\"#4A2800\"/><stop offset=\"100%\" stop-color=\"#150700\"/></linearGradient><linearGradient id=\"aband\" x1=\"0\" y1=\"0\" x2=\"0\" y2=\"1\"><stop offset=\"0%\" stop-color=\"#7A4500\"/><stop offset=\"100%\" stop-color=\"#2A1000\"/></linearGradient><radialGradient id=\"alens\" cx=\"35%\" cy=\"30%\"><stop offset=\"0%\" stop-color=\"#4A7AB5\"/><stop offset=\"100%\" stop-color=\"#0A1F3D\"/></radialGradient></defs></svg>' }} />
                    <div style={{ color: '#fff', fontSize: 22, fontWeight: 800 }}>GoldenScope</div>
          <div style={{ color: 'rgba(255,255,255,.5)', fontSize: 12, marginTop: 2 }}>
            Contract Manager Portal
          </div>
        </div>

        {/* Card */}
        <div style={{
          background: '#fff', borderRadius: 16, overflow: 'hidden',
          boxShadow: '0 32px 80px rgba(0,0,0,0.35)',
        }}>

          {/* Progress bar */}
          <div style={{ height: 4, background: '#f1f5f9' }}>
            <div style={{
              height: '100%',
              background: 'linear-gradient(90deg, #c49922, #e8c04a)',
              width: ((step / (STEPS.length - 1)) * 100) + '%',
              transition: 'width 0.4s ease',
            }} />
          </div>

          {/* Step dots */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, paddingTop: 16 }}>
            {STEPS.map((s, i) => (
              <div key={s.id} style={{
                width: i === step ? 20 : 8, height: 8, borderRadius: 99,
                background: i <= step ? '#0f1f3d' : '#e2e8f0',
                transition: 'all 0.3s ease',
              }} />
            ))}
          </div>

          <div style={{ padding: '24px 36px 32px' }}>

            {/* ── WELCOME STEP ── */}
            {current.id === 'welcome' && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 52, marginBottom: 16 }}>✒</div>
                <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f1f3d', marginBottom: 8 }}>
                  Welcome to GoldenScope
                </h2>

                {/* Penny greeting bubble */}
                <div style={{
                  background: '#f8fafc', border: '1.5px solid #e2e8f0',
                  borderRadius: 12, padding: '16px 20px', marginBottom: 20,
                  textAlign: 'left', position: 'relative',
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                      background: 'linear-gradient(135deg, #c49922, #e8c04a)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 14, fontWeight: 900, color: '#0f1f3d',
                    }}>P</div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#c49922', marginBottom: 4 }}>
                        Penny · AI Assistant
                      </div>
                      <div style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                        Hi! I'm Penny, your AI contract writing assistant. As a Contract Manager,
                        I'm here to help you draft a clear, compliant <strong>Statement of Work</strong> before
                        you submit a contract request.
                      </div>
                      <div style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7, marginTop: 8 }}>
                        I'll guide you through the <strong>Five W's</strong> framework, reference the
                        State Contracting Manual along the way, and help you build an Exhibit A SOW
                        that your contract analyst can pick up and run with.
                      </div>
                      <div style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7, marginTop: 8 }}>
                        You can also ask me questions anytime — I'm here throughout the entire process.
                        Let's get you set up!
                      </div>
                    </div>
                  </div>
                </div>

                {/* Feature pills */}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                  {[
                    ['📋', 'SOW Drafting'],
                    ['📖', 'SCM References'],
                    ['✅', 'Compliance Review'],
                    ['📄', 'Export to DOCX/PDF'],
                    ['💬', 'Ask Penny'],
                  ].map(([icon, label]) => (
                    <div key={label} style={{
                      padding: '5px 12px', background: '#f0f4ff',
                      borderRadius: 99, fontSize: 12, color: '#1e3a8a', fontWeight: 500,
                    }}>
                      {icon} {label}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── PROFILE STEP ── */}
            {current.id === 'profile' && (
              <div>
                <div style={{ fontSize: 36, marginBottom: 10, textAlign: 'center' }}>👤</div>
                <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f1f3d', marginBottom: 4, textAlign: 'center' }}>
                  Set Up Your Profile
                </h2>
                <p style={{ fontSize: 13, color: '#64748b', textAlign: 'center', marginBottom: 20, lineHeight: 1.6 }}>
                  Your profile will be visible to contract analysts when they assign you to a contract.
                  You can add multiple addresses for different office locations.
                </p>

                {error && (
                  <div style={{
                    padding: '10px 14px', borderRadius: 8, marginBottom: 16,
                    background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 13,
                  }}>{error}</div>
                )}

                {/* Basic info */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={labelStyle}>Full Name *</label>
                    <input style={inputStyle} type="text" value={name}
                      onChange={e => setName(e.target.value)} placeholder="e.g., Sean Grant" />
                  </div>
                  <div>
                    <label style={labelStyle}>Title</label>
                    <input style={inputStyle} type="text" value={title}
                      onChange={e => setTitle(e.target.value)} placeholder="e.g., Contract Manager II" />
                  </div>
                  <div>
                    <label style={labelStyle}>Division / Unit</label>
                    <input style={inputStyle} type="text" value={division}
                      onChange={e => setDivision(e.target.value)} placeholder="e.g., Operations Division" />
                  </div>
                  <div>
                    <label style={labelStyle}>Phone</label>
                    <input style={inputStyle} type="tel" value={phone}
                      onChange={e => setPhone(e.target.value)} placeholder="(916) 555-0100" />
                  </div>
                  <div>
                    <label style={labelStyle}>Email</label>
                    <input style={inputStyle} type="email" value={email}
                      onChange={e => setEmail(e.target.value)} placeholder="you@agency.ca.gov" />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={labelStyle}>Agency Name</label>
                    <input style={inputStyle} type="text" value={agencyName}
                      onChange={e => setAgencyName(e.target.value)} placeholder="e.g., Office of Energy Infrastructure Safety" />
                  </div>
                </div>

                {/* Addresses */}
                <div style={{ marginTop: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <label style={{ ...labelStyle, marginBottom: 0 }}>Office Addresses</label>
                    <button type="button" onClick={addAddress} style={{
                      padding: '4px 10px', borderRadius: 6, border: '1.5px solid #c49922',
                      background: '#fffbeb', color: '#92400e', fontSize: 11,
                      fontWeight: 600, cursor: 'pointer',
                    }}>+ Add Address</button>
                  </div>

                  {addresses.map((addr, i) => (
                    <div key={i} style={{
                      padding: '12px 14px', borderRadius: 10, marginBottom: 10,
                      border: '1.5px solid #e2e8f0', background: '#f8fafc',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <input
                          style={{ ...inputStyle, fontSize: 12, fontWeight: 600, width: '60%', padding: '6px 10px' }}
                          type="text" value={addr.label}
                          onChange={e => updateAddress(i, 'label', e.target.value)}
                          placeholder="Address label (e.g., Main Office)" />
                        {addresses.length > 1 && (
                          <button type="button" onClick={() => removeAddress(i)} style={{
                            background: 'none', border: 'none', color: '#dc2626',
                            fontSize: 12, cursor: 'pointer', fontWeight: 600,
                          }}>✕ Remove</button>
                        )}
                      </div>
                      <input style={{ ...inputStyle, marginBottom: 6, fontSize: 13 }}
                        type="text" value={addr.street}
                        onChange={e => updateAddress(i, 'street', e.target.value)}
                        placeholder="Street address" />
                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 6 }}>
                        <input style={{ ...inputStyle, fontSize: 13 }}
                          type="text" value={addr.city}
                          onChange={e => updateAddress(i, 'city', e.target.value)}
                          placeholder="City" />
                        <input style={{ ...inputStyle, fontSize: 13 }}
                          type="text" value={addr.state}
                          onChange={e => updateAddress(i, 'state', e.target.value)}
                          placeholder="State" />
                        <input style={{ ...inputStyle, fontSize: 13 }}
                          type="text" value={addr.zip}
                          onChange={e => updateAddress(i, 'zip', e.target.value)}
                          placeholder="ZIP" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── DONE STEP ── */}
            {current.id === 'done' && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 52, marginBottom: 16 }}>🎉</div>
                <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f1f3d', marginBottom: 8 }}>
                  You're All Set!
                </h2>

                {/* Penny sign-off */}
                <div style={{
                  background: '#f8fafc', border: '1.5px solid #e2e8f0',
                  borderRadius: 12, padding: '14px 18px', marginBottom: 20,
                  textAlign: 'left',
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{
                      width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                      background: 'linear-gradient(135deg, #c49922, #e8c04a)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 900, color: '#0f1f3d',
                    }}>P</div>
                    <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.7 }}>
                      Your profile is saved and visible to analysts at your agency.
                      Whenever you're ready, click <strong>"Start New SOW"</strong> and
                      I'll walk you through everything — one question at a time. 
                      Remember, you can ask me anything along the way!
                    </div>
                  </div>
                </div>

                {/* Summary chips */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, textAlign: 'left', marginBottom: 8 }}>
                  {[
                    [true,  '✅ Profile saved — analysts can find and assign you'],
                    [true,  '✅ Penny is ready with the State Contracting Manual'],
                    [true,  '✅ SOW drafting, compliance review, and export ready'],
                  ].map(([done, label], i) => (
                    <div key={i} style={{
                      display: 'flex', gap: 10, alignItems: 'center',
                      fontSize: 13, color: done ? '#15803d' : '#94a3b8',
                    }}>
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Action buttons ── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 28 }}>
              {step > 0 && current.id !== 'done' ? (
                <button type="button" onClick={() => setStep(s => s - 1)} style={{
                  background: 'none', border: 'none', color: '#94a3b8',
                  fontSize: 13, cursor: 'pointer', fontWeight: 600,
                }}>← Back</button>
              ) : <div />}

              <button
                type="button"
                onClick={handleNext}
                disabled={saving}
                style={{
                  padding: '11px 28px', borderRadius: 10, border: 'none',
                  background: saving ? '#d1b85a' : 'linear-gradient(135deg, #c49922, #e8c04a)',
                  color: '#0f1f3d', fontWeight: 700, fontSize: 14,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 14px rgba(196,153,34,0.3)',
                }}>
                {saving ? 'Saving…'
                  : current.id === 'welcome'  ? 'Get Started →'
                  : current.id === 'profile'  ? 'Save & Continue →'
                  : "Let's Go →"}
              </button>
            </div>

            {/* Skip profile */}
            {current.id === 'profile' && (
              <div style={{ textAlign: 'center', marginTop: 12 }}>
                <button type="button" onClick={() => setStep(s => s + 1)} style={{
                  background: 'none', border: 'none', fontSize: 12,
                  color: '#94a3b8', cursor: 'pointer', textDecoration: 'underline',
                }}>
                  Skip for now — I'll fill this in later
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Sign out */}
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <button onClick={onSignOut} style={{
            background: 'none', border: 'none', color: 'rgba(255,255,255,.3)',
            fontSize: 12, cursor: 'pointer',
          }}>Sign out</button>
        </div>

        <div style={{ textAlign: 'center', marginTop: 8, color: 'rgba(255,255,255,.2)', fontSize: 11 }}>
          Powered by Penny · GoldenScope © 2025
        </div>
      </div>
    </div>
  );
}

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 600,
  color: '#374151', marginBottom: 5,
};

const inputStyle = {
  width: '100%', padding: '9px 11px', borderRadius: 7,
  border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none',
  fontFamily: 'inherit', boxSizing: 'border-box',
  background: '#fff',
};
